import React from "react";
import styled from "styled-components";
import { Tag } from "@blueprintjs/core";
import { faFolder as faFolderOpen } from "@fortawesome/pro-regular-svg-icons";
import {
  faChevronDown,
  faChevronRight,
  faFile,
  faFolder,
  faHourglass,
  faLock,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, Tooltip } from "@mantine/core";

import { ContextMenu } from "@components";

import { HighlightedText } from "@pages/editor/components/FileBrowser/HighlightedText";

interface TagProps {
  depth: number;
}

const RightTag = styled(Tag)<TagProps>`
  flex-shrink: 0;
  background: var(--global-light-gray);
  color: var(--global-dark-gray);
  width: 36px;
  filter: hue-rotate(calc(${(props) => props.depth * 150}deg));
  text-align: center;
`;

const Title = styled.div`
  overflow-x: hidden;
  display: flex;
  align-items: center;
  gap: var(--global-spacing-s);
  flex-grow: 1;
`;

const TitleText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  flex-grow: 1;
`;

const ActionIcons = styled.div`
  display: none;
  position: absolute;
  right: 0;
  top: 0;
  gap: 8px;
  align-items: center;
  height: 100%;
  padding: 0 8px;
`;

const ClickableIcon = styled.div`
  cursor: pointer;
  color: var(--global-mid-gray);
  &:hover {
    color: black;
  }
`;

const LockedFileIcon = (
  <span className="fa-layers fa-fw" style={{ display: "inline-block" }}>
    <FontAwesomeIcon fixedWidth icon={faFile} color="var(--global-dark-gray)" />
    <FontAwesomeIcon
      fixedWidth
      icon={faLock}
      transform={"shrink-9 right-1 down-3"}
      color="var(--global-light-gray)"
    />
  </span>
);

interface ContextAction {
  actionName: string;
  action: (event: React.MouseEvent) => void;
  icon: React.ReactNode;
}

interface Props {
  type: "file" | "private-file" | "folder-open" | "folder-closed" | "loading";
  title: string;
  onClick?: () => void;
  contextMenuActions?: ContextAction[];
  depth?: number;
  childCount?: number;
  searchQuery?: string;
  isLoading?: boolean;
  isSelected: boolean;
}

interface ContainerProps {
  isClickable?: boolean;
}

const Container = styled.div<{ isClickable: boolean; isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--global-spacing-m);
  cursor: ${(props: ContainerProps) =>
    props.isClickable ? "pointer" : "default"};
  &:hover .action-items {
    display: flex;
    /* this needs to be same color as the background of the container */
    background-color: ${({ isSelected }) =>
      isSelected ? "var(--indigo7)" : "var(--light-gray4)"};
  }
  :hover .right-tag {
    display: none;
  }
`;

const generateIcon = (type: string) => {
  if (type === "private-file") return LockedFileIcon;

  const icon =
    type === "file"
      ? faFile
      : type === "folder-open"
      ? faFolderOpen
      : type === "folder-closed"
      ? faFolder
      : type === "loading"
      ? faHourglass
      : null;

  return (
    icon && (
      <FontAwesomeIcon fixedWidth icon={icon} color="var(--global-dark-gray)" />
    )
  );
};

export const NodeLabel = ({
  type,
  title,
  onClick,
  contextMenuActions = [],
  depth = 0,
  childCount,
  searchQuery = "",
  isLoading = false,
  isSelected,
}: Props) => {
  const actionIconsItems = contextMenuActions.filter(({ icon }) => !!icon);
  return (
    <ContextMenu
      menuProps={{ width: "max-content" }} // don't wrap menu items
      dropdown={
        contextMenuActions.length === 0 ? (
          <Menu.Item disabled>No actions available</Menu.Item>
        ) : (
          contextMenuActions.map(({ actionName, action, icon }) => (
            <Menu.Item key={actionName} icon={icon} onClick={action}>
              {actionName}
            </Menu.Item>
          ))
        )
      }
      target={
        <Container
          data-test={"node-label-" + type}
          aria-label={title}
          onClick={onClick}
          isClickable={onClick !== undefined}
          isSelected={isSelected}
        >
          <Title title={title}>
            {childCount !== undefined ? (
              <FontAwesomeIcon
                fixedWidth
                icon={type === "folder-open" ? faChevronDown : faChevronRight}
                color="var(--gray3)"
                style={{
                  flexShrink: 0,
                }}
              />
            ) : null}
            <div
              style={{
                flexShrink: 0,
              }}
            >
              {generateIcon(type)}{" "}
            </div>
            <TitleText>
              <HighlightedText fullText={title} highlightedText={searchQuery} />
            </TitleText>
          </Title>

          {isLoading || (childCount && childCount === -1) ? (
            <RightTag
              className="right-tag"
              title="loading contents"
              depth={depth}
            >
              <FontAwesomeIcon
                icon={faSpinner}
                pulse
                style={{ color: "var(--global-blue-gray)" }}
              />
            </RightTag>
          ) : childCount !== undefined ? (
            <RightTag
              className="right-tag"
              title={`${childCount} items in this folder`}
              depth={depth}
            >
              {childCount}
            </RightTag>
          ) : null}
          {actionIconsItems.length > 0 && (
            <ActionIcons className="action-items">
              {actionIconsItems.map(({ icon, action, actionName }) => {
                return (
                  <Tooltip
                    label={actionName}
                    position="top"
                    key={actionName}
                    withArrow
                    withinPortal
                  >
                    <ClickableIcon
                      role="button"
                      aria-label={actionName}
                      onClick={(e) => {
                        action(e);
                        e.stopPropagation();
                      }}
                    >
                      {icon}
                    </ClickableIcon>
                  </Tooltip>
                );
              })}
            </ActionIcons>
          )}
        </Container>
      }
    />
  );
};
